import React, { useEffect, useState } from 'react';
import SearchBar from '../../../components/SearchBar/SearchBar';
import MLButton from '../../../components/MLButton';
import ExportDialog from '../../../components/ExportDialog/ExportDialog';
import { useExportCourseTotal } from '../../hooks/useExportCourseTotal';
import styles from './StudentCourseTotalContent.module.css';
import MLIcon from '../../../components/MLIcon';
import { StudentCourseTotalTable } from './StudentCourseTotalTable';
import { LOW_SCORE_THRESHOLD_PERCENT } from '../../constants';
import {
  filterStudentsBySearch,
  formatWeekDate,
} from '../../utils/studentCourseTotalUtils';

export const StudentCourseTotalContent = ({
  students,
  lowScoringStudentCount,
  currentWeekDate,
  previousWeekDate,
  handleISGLaunch,
  setOpen,
  courseId,
  apiHost,
}) => {
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const filteredStudents = filterStudentsBySearch(students, searchValue);
  const currentWeekLabel = formatWeekDate(currentWeekDate);
  const previousWeekLabel = formatWeekDate(previousWeekDate);

  const exportQuery = useExportCourseTotal(apiHost, courseId, shouldFetch);

  const resetExportState = () => {
    setIsExportLoading(false);
    setShowExportDialog(false);
    setShouldFetch(false);
  };

  useEffect(() => {
    if (!exportQuery.isLoading && shouldFetch) {
      resetExportState();
    }
  }, [exportQuery.isLoading, shouldFetch]);

  const handleConfirmedExport = () => {
    setIsExportLoading(true);
    setShouldFetch(true);
  };

  const handleCancelExport = () => {
    resetExportState();
  };

  const handleExportClick = () => {
    setShowExportDialog(true);
  };

  const renderHeaderWarning = () => {
    const studentCounter =
      lowScoringStudentCount === 1 ? 'student has' : 'students have';

    return (
      <div className={styles.headerWarning}>
        <MLIcon type="warning" className={styles.warningIcon} />
        <div
          className={styles.warningText}
          data-test-id="course_total_warning_text"
        >
          {lowScoringStudentCount} {studentCounter} a course total of{' '}
          {LOW_SCORE_THRESHOLD_PERCENT}% or below.
        </div>
      </div>
    );
  };

  const renderHeaderDescription = () => {
    return (
      <div className={styles.headerDescription}>
        The list below shows all the students enrolled in your course with their
        Achieve course totals for the last two weeks.{' '}
        <strong>
          Students with a recent course total below{' '}
          {LOW_SCORE_THRESHOLD_PERCENT}% are marked with the alert icon.
        </strong>
      </div>
    );
  };

  const renderSearchAndExportButton = () => {
    return (
      <div className={styles.searchAndExportContainer}>
        <SearchBar
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          testId="course_total_alert_search_students_bar"
        />
        <MLButton
          btnType="secondary"
          btnText="Export List"
          onClick={handleExportClick}
          testId="course_total_alert_export_list_btn"
        />
      </div>
    );
  };

  return (
    <div className={styles.contentContainer}>
      <ExportDialog
        show={showExportDialog}
        onClose={handleCancelExport}
        onExport={handleConfirmedExport}
        onCancel={handleCancelExport}
        isLoading={isExportLoading}
        title="Export Course Totals"
        description="This export includes the name and email of every student in the course along with their two most recent course totals. The list is exported as a CSV file."
        loadingMessage="We're exporting the course totals. This could take a few minutes to complete."
        confirmExportTestId="course_total_alert_confirm_export_btn"
        cancelExportTestId="course_total_alert_cancel_export_btn"
        cancelDuringExportTestId="course_total_alert_cancel_during_export_btn"
      />
      {renderHeaderWarning()}
      {renderHeaderDescription()}
      {renderSearchAndExportButton()}
      <StudentCourseTotalTable
        students={filteredStudents}
        currentWeekLabel={currentWeekLabel}
        previousWeekLabel={previousWeekLabel}
        handleISGLaunch={handleISGLaunch}
        setOpen={setOpen}
      />
    </div>
  );
};
